import { type ReactNode } from "react";
import { Card } from "@/shared/components/ui/Card";
import { Logo } from "@/shared/components/ui/Logo";

interface AuthCardProps {
  /** Title shown below the logo. */
  title?: ReactNode;
  /** Optional subtitle under the title. */
  subtitle?: ReactNode;
  /** Optional element above the logo in the brand header (e.g. a back link or 404 code). */
  brandTop?: ReactNode;
  /** Footer spanning the full card width below the content (e.g. reCAPTCHA notice). */
  footer?: ReactNode;
  /** Main content — the form or status — shown below the brand header. */
  children: ReactNode;
  className?: string;
  "data-testid"?: string | undefined;
}

/**
 * Single-column authentication card.
 *
 * Brand header (left-aligned, on top): logo + title + subtitle.
 * Content (below): the page's form or status.
 * Sizing follows the minimal-login-form guide (8-point grid, 32px title).
 * The logo is swappable via {@link Logo} (skin asset).
 */
export function AuthCard({
  title,
  subtitle,
  brandTop,
  footer,
  children,
  className,
  "data-testid": dataTestId,
}: AuthCardProps) {
  return (
    <Card className={className} data-testid={dataTestId} footer={footer}>
      {/* Brand header */}
      <div className="flex flex-col items-start text-left">
        {brandTop && <div className="mb-4 w-full">{brandTop}</div>}
        <Logo className="mb-6 h-10 lg:h-12" />
        {title && (
          <h1 className="text-ink text-[2rem] leading-[1.1] font-bold tracking-tight text-balance">
            {title}
          </h1>
        )}
        {subtitle && <p className="mt-2 max-w-[34ch] text-base text-gray-500">{subtitle}</p>}
      </div>

      {/* Content — 24px (3x) group divide below the brand header */}
      <div className="mt-6">{children}</div>
    </Card>
  );
}
